import { useQuery } from '@apollo/client'
import { Link } from '@faceup/router'
import { Card, ContentLayout } from '@faceup/ui'
import { Col, Input, MultiSelect, Row, Space, Table, type TableColumns } from '@faceup/ui-base'
import {
  ChargebeeSubscriptionStatus,
  convertRegionToRegionName,
  PhoneNumberStatus,
  type Region,
  regionToRegionNameMap,
  SupportedRegion,
} from '@faceup/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useMemo, useState } from 'react'
import { graphql } from '../__generated__'
import { InputLabel } from '../Components/InputLabel'

const query = graphql(`
  query ExternalInternalHotlinesQuery {
    externalInternalHotlines {
      items {
        id
        regionId
        phone
        name
        status
        institution {
          id
          organizationalUnitName
          billing {
            id
            subscriptionStatus
          }
        }
      }
    }
  }
`)

const statusMessages: Record<PhoneNumberStatus, string> = {
  [PhoneNumberStatus.Everywhere]: '✅',
  [PhoneNumberStatus.DatabaseOnly]:
    'Phone number is in local database only - Hotline will not work',
  [PhoneNumberStatus.MissingFromPhoneDiscovery]:
    'Phone number is missing from Phone Discovery - Hotline will not work',
  [PhoneNumberStatus.MissingFromVonage]:
    'Phone number is missing from Vonage - Hotline will not work',
  [PhoneNumberStatus.VonageOnly]:
    'Phone number is bought only in Vonage - We are paying without use',
  [PhoneNumberStatus.PhoneDiscoveryOnly]:
    'Phone number is only in Phone Discovery - Hotline will not work',
  [PhoneNumberStatus.MissingFromDatabase]:
    'Phone number is missing from local database - maybe not an error, but different region',
  [PhoneNumberStatus.Nowhere]: 'Phone number not found anywhere',
}

const VonageHotlines = () => {
  const [statusFilter, setStatusFilter] = useState<PhoneNumberStatus[]>([])
  const [billingStatusFilter, setBillingStatusFilter] = useState<ChargebeeSubscriptionStatus[]>([])
  const [phoneFilter, setPhoneFilter] = useState('')
  const [institutionFilter, setInstitutionFilter] = useState('')
  const [regionFilter, setRegionFilter] = useState<SupportedRegion[]>([])

  const { data, loading } = useQuery(query)

  const hotlines = data?.externalInternalHotlines?.items ?? []

  const filteredHotlines = useMemo(() => {
    return hotlines.filter(hotline => {
      if (statusFilter.length > 0 && !statusFilter.includes(hotline.status)) {
        return false
      }

      if (
        billingStatusFilter.length > 0 &&
        (!hotline.institution?.billing?.subscriptionStatus ||
          !billingStatusFilter.includes(hotline.institution.billing.subscriptionStatus))
      ) {
        return false
      }

      if (phoneFilter && !hotline.phone?.toLowerCase().includes(phoneFilter.toLowerCase())) {
        return false
      }

      if (
        institutionFilter &&
        !hotline.institution?.organizationalUnitName
          ?.toLowerCase()
          .includes(institutionFilter.toLowerCase())
      ) {
        return false
      }

      if (
        regionFilter.length > 0 &&
        (!hotline.regionId || !regionFilter.includes(hotline.regionId as SupportedRegion))
      ) {
        return false
      }

      return true
    })
  }, [hotlines, statusFilter, billingStatusFilter, phoneFilter, institutionFilter, regionFilter])

  const columns: TableColumns<
    NonNullable<ResultOf<typeof query>['externalInternalHotlines']>['items'][number]
  > = [
    {
      title: 'Status',
      key: 'status',
      render: record => (
        <span
          className={
            record.status !== PhoneNumberStatus.Everywhere ? 'font-bold text-red5' : undefined
          }
        >
          {statusMessages[record.status]}
        </span>
      ),
    },
    {
      title: 'Billing status',
      key: 'billingStatus',
      render: record => {
        const status = record.institution?.billing?.subscriptionStatus

        return (
          <span
            className={
              status === ChargebeeSubscriptionStatus.Cancelled ? 'font-bold text-red5' : ''
            }
          >
            {status ?? '-'}
          </span>
        )
      },
    },
    {
      title: 'Phone',
      key: 'phone',
      render: ({ phone }) =>
        phone === '+15074322387' ||
        phone === '+420234674152' ||
        phone === '+420225636014' ||
        phone === '+420296216767'
          ? `${phone} (internal, do not delete)`
          : phone,
    },
    {
      title: 'Institution',
      key: 'institution',
      render: record =>
        record.institution?.id ? (
          <Link to={routes => routes.institution({ id: record.institution?.id ?? '' })}>
            {record.institution?.organizationalUnitName ?? '-'}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      title: 'Hotline name',
      key: 'name',
      render: record => record.name ?? '-',
    },
    {
      title: 'Region',
      key: 'regionId',
      render: record =>
        record.regionId
          ? (regionToRegionNameMap[record.regionId as Region] ?? record.regionId ?? '-')
          : '-',
    },
  ]

  return (
    <ContentLayout header={<ContentLayout.Header title='Vonage Hotlines' />}>
      <Space direction='vertical' size='large' style={{ width: '100%' }}>
        <Card>
          <Row gutter={16} style={{ padding: '24px' }}>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Status</InputLabel>
              <MultiSelect
                style={{ width: '100%' }}
                allowClear
                placeholder='All statuses'
                onChange={(value: PhoneNumberStatus[]) => setStatusFilter(value)}
                options={Object.values(PhoneNumberStatus).map(status => ({
                  label: statusMessages[status],
                  value: status,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Billing Status</InputLabel>
              <MultiSelect
                style={{ width: '100%' }}
                allowClear
                placeholder='All billing statuses'
                onChange={(value: ChargebeeSubscriptionStatus[]) => setBillingStatusFilter(value)}
                options={Object.values(ChargebeeSubscriptionStatus).map(status => ({
                  label: status,
                  value: status,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Phone</InputLabel>
              <Input
                placeholder='Search phone'
                onChange={({ target: { value } }) => setPhoneFilter(value)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Institution</InputLabel>
              <Input
                placeholder='Search institution'
                onChange={({ target: { value } }) => setInstitutionFilter(value)}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <InputLabel>Region</InputLabel>
              <MultiSelect
                style={{ width: '100%' }}
                allowClear
                placeholder='All regions'
                onChange={value => setRegionFilter(value)}
                options={Object.values(SupportedRegion).map(region => ({
                  label: convertRegionToRegionName(region),
                  value: region,
                }))}
              />
            </Col>
          </Row>
        </Card>
        <Table
          shadow
          loading={loading}
          rowKey='id'
          dataSource={filteredHotlines}
          locale={{ emptyText: ' ' }}
          scroll={{ x: 'max-content' }}
          columns={columns}
          pagination={{ pageSize: 1000 }}
        />
      </Space>
    </ContentLayout>
  )
}

export default VonageHotlines
